import { subscribe } from 'valtio'
import { throttle } from 'lodash-es'

import { registerFatalDisconnect } from '../../FatalError/FatalError'
import { type Floor } from '../../firebase/model/floor-model'
import { isWebinarLiveState } from '../../firebase/hooks/useWebinarGlobalState'
import { request } from '../../global/api/legacyApi'
import isHeadless from '../../headless/isHeadless'
import { reloadRoom } from '../../headless/src/reload'
import { logMessage } from '../../Logging/log'
import {
  getConnectionStats,
  getParticipants,
  getProviderInfo,
  getRoomStatus,
  getSelectedTrackOnStage,
  roomState,
  type Track,
} from '../webRTCRoom'
import {
  getIsClientSecondCam,
  getLiveId,
  getRole,
} from '../../global/blocs/roomInfo'
import { floorState } from '../../firebase/hooks/useFloor'
import { devicesState } from '../../Devices/useDevices'
import { type PanelState, panelState } from '../../firebase/hooks/usePanel'
import { HTTPMethod } from '../../sharedLegacy/utils/api.types'

type TrackInfo = null | {
  id: string
  deviceId?: string
  label: string
  isEnabled: boolean
  isMuted: boolean
  capabilities: MediaTrackCapabilities
  constraints: MediaTrackConstraints
  settings: MediaTrackSettings
  stats: unknown
  state: 'live' | 'ended'
}

type ProviderInfo = {
  providerName: string
  identity: string
  sid: string
  state: string
  media_region: string
  signaling_region: string
  participant_state: string
  customExtras: Record<string, unknown>
}

type TrackTypeConnectionInfo = {
  send?: {
    qualityLevel: number
    bandwidthAvailable: number | null
    bandwidthActual: number | null
    bandwidthFractionLost: number | null
  }
  receive?: {
    qualityLevel: number
    bandwidthAvailable: number | null
    bandwidthActual: number | null
    bandwidthFractionLost: number | null
  }
}

type WebRTCConnectionStats = {
  qualityLevel: number | null
  qualityStats: unknown
  type: string
  protocol: string
  trackStats: unknown
  audioStats: TrackTypeConnectionInfo | null
  videoStats: TrackTypeConnectionInfo | null
}

type NavigatorInfo = {
  isOnline: boolean
  userAgent: string
  network: {
    downlink: number
    downlinkMax: number
    effectiveType: string
    rtt: number
    saveData: boolean
    /** defaults to "unknown" */
    type: string
  }
  battery: {
    charging: boolean | 'unknown'
    chargingTime: number | 'unknown'
    dischargingTime: number | 'unknown'
    level: number | 'unknown'
  } | null
  /** navigator.mediaDevices.enumerateDevices — use deviceSelection bloc */
  mediaDevices: {
    id: string
    name: string
    deviceId: string
    capabilities: MediaTrackCapabilities
  }[]
}

type Heartbeat = {
  timestamp: number
  version: string
  release: string
  webinar: {
    liveId: number
    liveSpeakerId: number | null
    isLive: boolean
    role: 'headless' | 'speaker' | 'producer' | '2ndcam'
    roomStatus: string
  }
  navigator: NavigatorInfo | null
  deviceSelection: Record<MediaDeviceInfo['kind'], string | undefined>
  provider: ProviderInfo | null
  webrtcConnection: WebRTCConnectionStats | null
  stage: {
    floor: Floor | null
    panel: PanelState
    selectedTracks: string[]
  }
  localTracks: {
    camera: TrackInfo
    cameraAudio: TrackInfo
    screen: TrackInfo
    screenAudio: TrackInfo
    mobile: TrackInfo
  } | null
  remoteTracks: {
    participantId: number
    camera: TrackInfo
    cameraAudio: TrackInfo
    screen: TrackInfo
    screenAudio: TrackInfo
    mobile: TrackInfo
  }[]
}

const timeout = <T>(
  promise: Promise<T>,
  maxTime: number,
  onTimeout?: () => void
) => {
  return new Promise<T | null>((resolve, reject) => {
    const t = setTimeout(() => {
      onTimeout?.()
      resolve(null)
    }, maxTime)
    promise
      .then((v) => {
        clearTimeout(t)
        resolve(v)
      })
      .catch(reject)
  })
}

const mapTrackToTrackInfo = (track: Track | null): TrackInfo => {
  if (!track) return null

  const { mediaStream } = track
  const { deviceId } = mediaStream.getSettings()
  // Stats are not supported in all browsers
  // More info at: https://w3c.github.io/mediacapture-extensions/#mediastreamtrack-statistics
  const stats = 'stats' in mediaStream ? mediaStream.stats : null

  return {
    id: track.mediaStream.id,
    deviceId,
    label: mediaStream.label,
    isEnabled: track.isEnabled,
    isMuted: mediaStream.muted,
    capabilities: mediaStream.getCapabilities(),
    settings: mediaStream.getSettings(),
    constraints: mediaStream.getConstraints(),
    stats,
    state: mediaStream.readyState,
  } as const
}

const mapRole = () => {
  const role = getRole()
  const isSecondCam = getIsClientSecondCam()

  if (role === 'audience') throw new Error('Audience should not send heartbeat')

  if (isSecondCam) {
    return '2ndcam'
  }

  switch (role) {
    case 'presenter':
      return 'speaker'
    default:
      return role
  }
}

const mapStageInfo = () => {
  const { floor } = floorState

  const selectedTracksOnStage = getSelectedTrackOnStage()
  return {
    floor,
    panel: panelState,
    selectedTracks: selectedTracksOnStage,
  }
}

const mapDevicesToMediaDevicesInfo = () => {
  const { devices } = devicesState

  return Object.entries(devices).flatMap(([id, devicesInfo]) => {
    return devicesInfo.map((device) => ({
      id,
      deviceId: device.deviceId,
      name: device.label,
      // Some devices might not have capabilities (e.g. audio output devices) or some browsers might not support it.
      capabilities: 'getCapabilities' in device ? device.getCapabilities() : {},
    }))
  })
}

const mapNavigatorInfo = async () => {
  const mediaDevicesInfo = mapDevicesToMediaDevicesInfo()
  // @ts-expect-error — not supported across all browsers
  const { connection } = navigator
  let battery

  if ('getBattery' in navigator && typeof navigator.getBattery === 'function') {
    try {
      battery = (await timeout(navigator.getBattery(), 100)) as {
        charging: boolean
        chargingTime: number
        dischargingTime: number
        level: number
      }
    } catch (e) {
      // do nothing if not supported
    }
  }

  return {
    isOnline: navigator.onLine,
    userAgent: navigator.userAgent,
    network: {
      downlink: connection?.downlink ?? 'unknown',
      downlinkMax: connection?.downlinkMax ?? 'unknown',
      effectiveType: connection?.effectiveType ?? 'unknown',
      rtt: connection?.rtt ?? 'unknown',
      saveData: connection?.saveData ?? 'unknown',
      type: connection?.type ?? 'unknown',
    },
    battery: {
      charging: battery?.charging ?? 'unknown',
      chargingTime: battery?.chargingTime ?? 'unknown',
      dischargingTime: battery?.dischargingTime ?? 'unknown',
      level: battery?.level ?? 'unknown',
    },
    mediaDevices: mediaDevicesInfo,
  } as const satisfies NavigatorInfo
}

const mapRemoteTracks = () => {
  const participants = getParticipants((p) => !p.isLocal)
  return participants.map((participant) => {
    return {
      participantId: participant.id,
      camera: mapTrackToTrackInfo(participant.tracks.camera),
      cameraAudio: mapTrackToTrackInfo(participant.tracks.cameraAudio),
      screen: mapTrackToTrackInfo(participant.tracks.screen),
      screenAudio: mapTrackToTrackInfo(participant.tracks.screenAudio),
      mobile: mapTrackToTrackInfo(participant.tracks.mobile),
    }
  })
}

let heartbeatFailureStartTimestamp = Infinity

const getHeartbeatInfo = async (): Promise<Heartbeat> => {
  const localParticipant = getParticipants((p) => p.isLocal).at(0)
  const isWebinarLive = isWebinarLiveState()
  const liveId = getLiveId()
  const roomStatus = getRoomStatus()
  const stageInfo = mapStageInfo()
  const remoteTracks = mapRemoteTracks()
  const deviceSelection = devicesState.currentSelectedDevice

  const [providerInfo, navigatorInfo, webrtcConnectionStats] =
    await Promise.all([
      timeout(getProviderInfo(), 1000, () => {
        if (roomState.status === 'connected') {
          logMessage('TIMED OUT GETTING WEBRTC STATS', { level: 'error' })
        }
      }),
      timeout(mapNavigatorInfo(), 1000),
      timeout(getConnectionStats(), 1000),
    ])

  if (webrtcConnectionStats === null && roomState.status === 'connected') {
    logMessage('heartbeat is missing webrtc stats', {
      level: getRole() === 'headless' ? 'error' : 'warning',
    })

    if (heartbeatFailureStartTimestamp > Date.now()) {
      heartbeatFailureStartTimestamp = Date.now()
    }
  } else {
    heartbeatFailureStartTimestamp = Infinity
  }

  if (isHeadless && Date.now() > heartbeatFailureStartTimestamp + 5000) {
    reloadRoom(`Missing webrtc stats for heartbeats`)
  }

  const role = mapRole()
  return {
    timestamp: Date.now(),
    version: 'february-2025',
    release: build.SHA_SIGNATURE ?? 'local',
    webinar: {
      liveId,
      liveSpeakerId: localParticipant?.id ?? null,
      isLive: isWebinarLive,
      role,
      roomStatus,
    },
    navigator: navigatorInfo,
    deviceSelection,
    provider: providerInfo
      ? {
          providerName: providerInfo.providerName,
          identity: providerInfo.identity,
          sid: providerInfo.sid,
          state: providerInfo.state,
          media_region: providerInfo.media_region,
          signaling_region: providerInfo.signaling_region,
          participant_state: providerInfo.participant_state,
          customExtras: providerInfo.customExtras,
        }
      : null,
    webrtcConnection: webrtcConnectionStats,
    stage: stageInfo,
    localTracks: localParticipant
      ? {
          camera: mapTrackToTrackInfo(localParticipant.tracks.camera),
          cameraAudio: mapTrackToTrackInfo(localParticipant.tracks.cameraAudio),
          screen: mapTrackToTrackInfo(localParticipant.tracks.screen),
          screenAudio: mapTrackToTrackInfo(localParticipant.tracks.screenAudio),
          mobile: mapTrackToTrackInfo(localParticipant.tracks.mobile),
        }
      : null,
    remoteTracks,
  }
}

const reportHeartbeat = throttle(
  async () => {
    const heartbeatInfo = await getHeartbeatInfo()

    const url = '/api/2/live/webinar/participant-heartbeat'
    request({
      url,
      method: HTTPMethod.POST,
      data: {
        live_id: heartbeatInfo.webinar.liveId,
        live_speaker_id: heartbeatInfo.webinar.liveSpeakerId,
        data: JSON.stringify(heartbeatInfo),
      },
    }).catch((err: unknown) => {
      logMessage('Failed to send heartbeat', {
        level: 'warning',
        extra: { reason: err },
      })
    })
  },
  1e3,
  { leading: true, trailing: true }
)

/// ////////////////////////////// ///
/// initialization and destruction ///
/// ////////////////////////////// ///

const subscriptions: (() => void)[] = []

const init = () => {
  const interval = setInterval(reportHeartbeat, 10e3)
  registerFatalDisconnect(() => clearInterval(interval))
  subscriptions.push(() => clearInterval(interval))
  subscriptions.push(subscribe(devicesState, reportHeartbeat))
  subscriptions.push(subscribe(floorState, reportHeartbeat))
  subscriptions.push(subscribe(panelState, reportHeartbeat))
  subscriptions.push(subscribe(roomState, reportHeartbeat))
}

const destroy = () => {
  while (subscriptions.length > 0) {
    subscriptions.shift()?.()
  }
}

export {
  init,
  destroy,
  getHeartbeatInfo,
  type WebRTCConnectionStats,
  type ProviderInfo,
}
